import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { formatDate, getAvatarUrl, getImageUrl } from "src/Common/common";
import { can, handleError } from "src/Common/function";
import WOFormService from "src/Services/API/woform.service";
import UserService from "src/Services/API/user.service";
import Button from "src/Components/Button";
import CreateButton from "src/Components/CreateButton";
import FormCreateModal from "src/Pages/WO/Components/forms/FormCreateModal";
import FormViewerModal from "src/Pages/WO/Components/forms/FormViewerModal";
import FormFilterView from "./FormFilterView";
import AssignFormModal from "./components/AssignFormModal";
import UnassignFormModal from "./components/UnassignFormModal";
import DeleteFormModal from "./components/DeleteFormModal";
import DraftOptions from "./components/options/DraftOptions";
import FilledOptions from "./components/options/FilledOptions";
import AssignedOptions from "./components/options/AssignedOptions";
import { FORM_TYPES } from "src/Pages/forms/templates/components/FormTypeDropdown";
import { useNavigate } from "react-router-dom";
import PartiallyFilledOptions from "./components/options/PartiallyFilledOptions";

const FREQUENCIES = [
  { value: 0, label: "One Time" },
  { value: 1, label: "Weekly" },
  { value: 2, label: "Monthly" },
  { value: 3, label: "Quarterly" },
  { value: 4, label: "Semi-Annual" },
  { value: 5, label: "Annual" },
];

const STATUS_MAP = {
  0: { label: "Not Assigned", bg: "bg-[#D5D5D5]", text: "text-[#555]" }, // 0: Not Assigned
  1: { label: "Assigned", bg: "bg-[#F5C97E]", text: "text-[#7A5A1E]" }, // 1: Assigned
  2: { label: "Partially Filled", bg: "bg-[#D6BCFA]", text: "text-[#5B2C83]" }, // 2: Partially Filled
  3: { label: "Submitted", bg: "bg-[#A8D5A2]", text: "text-[#2D6A2E]" }, // 3: Submitted
};

const getFormTypeLabel = (value) => FORM_TYPES.find((t) => t.value === value)?.label || "";

const getFrequencyLabel = (value) => FREQUENCIES.find((f) => f.value === Number(value))?.label || "";

const getStatus = (value) => STATUS_MAP[value] || { label: "-", bg: "bg-gray-200", text: "text-gray-600" };

const AllForms = ({ hideControls = false, customerId }) => {
  const navigate = useNavigate();
  const canCreate = can("form:create");
  const canUpdate = can("form:update");
  const canDelete = can("form:delete");
  const canDuplicate = can("form:duplicate");
  const canAssign = can("form:assign");
  const canUnassign = can("form:unassign");
  const [forms, setForms] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [actionMenuId, setActionMenuId] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedFormForAssign, setSelectedFormForAssign] = useState(null);
  const [unassignModalOpen, setUnassignModalOpen] = useState(false);
  const [selectedFormForUnassign, setSelectedFormForUnassign] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedFormForDelete, setSelectedFormForDelete] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedFormForEdit, setSelectedFormForEdit] = useState(null);
  const [selectedWo, setSelectedWo] = useState(null);
  const [formViewerModalShown, setFormViewerModalShown] = useState(false);
  const [formsToPreview, setFormsToPreview] = useState([]);

  const fetchForms = (filters = {}) => {
    const mergedFilters = { ...filters };
    if (customerId) mergedFilters.customerId = customerId;
    WOFormService.getAllForms(mergedFilters)
      .then((response) => {
        if (response.status === 200) {
          setForms(response.data);
        }
      })
      .catch((error) => handleError(error));
  };

  useEffect(() => {
    fetchForms(appliedFilters);
  }, [customerId]);

  const handleApplyFilter = (filters) => {
    setAppliedFilters(filters);
    fetchForms(filters);
  };

  const handleClearFilter = () => {
    setAppliedFilters({});
    fetchForms({});
  };

  const handleCreateWo = (form) => {
    setSelectedFormForAssign(form);
    setSelectedWo(
      form?.wo
        ? {
            id: form.wo.id,
            number: form.wo.number,
            description: form.wo.description || "",
          }
        : null,
    );
    setAssignModalOpen(true);
    setActionMenuId(null);
  };

  const closeAssignModal = () => {
    setAssignModalOpen(false);
    setSelectedFormForAssign(null);
    setSelectedWo(null);
  };

  const handleAssignWo = () => {
    if (!selectedFormForAssign?.id || !selectedWo?.id) return;

    WOFormService.assign(selectedFormForAssign.id, selectedWo.id)
      .then(() => {
        toast("Form assigned successfully");
        closeAssignModal();
        fetchForms(appliedFilters);
      })
      .catch((error) => handleError(error));
  };

  const handleDuplicate = (form) => {
    if (!form) return;

    const currentUser = UserService.getCurrentUser();

    WOFormService.duplicate(form.id, currentUser?.id)
      .then(() => {
        toast("Form duplicated successfully");
        setActionMenuId(null);
        fetchForms(appliedFilters);
      })
      .catch((error) => handleError(error));
  };

  const handleDeleteClick = (form) => {
    setSelectedFormForDelete(form);
    setDeleteModalOpen(true);
    setActionMenuId(null);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedFormForDelete(null);
  };

  const handleDelete = () => {
    if (!selectedFormForDelete?.id) return;

    WOFormService.deleteById(selectedFormForDelete.id)
      .then(() => {
        toast("Form deleted successfully");
        closeDeleteModal();
        fetchForms(appliedFilters);
      })
      .catch((error) => handleError(error));
  };

  const handleEditClick = (form) => {
    setSelectedFormForEdit(form);
    setEditModalOpen(true);
    setActionMenuId(null);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setSelectedFormForEdit(null);
  };

  const handleEditSubmit = ({ formName, formType, frequency }) => {
    if (!selectedFormForEdit?.id) return false;

    return WOFormService.updateDetails(selectedFormForEdit.id, {
      name: formName,
      formType,
      frequency: formType === 0 ? frequency : null,
    })
      .then(() => {
        toast("Form updated successfully");
        fetchForms(appliedFilters);
        return true;
      })
      .catch((error) => {
        handleError(error);
        return false;
      });
  };

  const handleUnassignClick = (form) => {
    setSelectedFormForUnassign(form);
    setUnassignModalOpen(true);
    setActionMenuId(null);
  };

  const closeUnassignModal = () => {
    setUnassignModalOpen(false);
    setSelectedFormForUnassign(null);
  };

  const handleUnassign = () => {
    if (!selectedFormForUnassign?.id) return;

    WOFormService.unassign(selectedFormForUnassign.id)
      .then(() => {
        toast("Form unassigned successfully");
        closeUnassignModal();
        fetchForms(appliedFilters);
      })
      .catch((error) => handleError(error));
  };

  const handleFormClick = (form) => {
    if (form.status === 1 || form.status === 2 || form.status === 3) {
      setFormsToPreview([form]);
      setFormViewerModalShown(true);
      return;
    }

    if (form.status !== 0) return;

    navigate("/form-builder", {
      state: {
        formId: form.id,
        formName: form.name,
        formSchema: form.schema,
        formType: form.formType,
        frequency: form.frequency,
        templateId: form.template?.id,
        from: "/all-forms",
      },
    });
  };

  return (
    <div className="w-full h-full flex flex-col min-h-0 bg-backgroundColor">
      {createModalOpen && <FormCreateModal shown={createModalOpen} onClose={() => setCreateModalOpen(false)} />}
      {editModalOpen && (
        <FormCreateModal
          shown={editModalOpen}
          mode="edit"
          initialName={selectedFormForEdit?.name ?? ""}
          initialFormType={selectedFormForEdit?.formType ?? null}
          initialFrequency={
            selectedFormForEdit?.frequency !== null && selectedFormForEdit?.frequency !== undefined
              ? String(selectedFormForEdit.frequency)
              : ""
          }
          showTemplateField={false}
          showFrequencyField={false}
          onSubmit={handleEditSubmit}
          onClose={closeEditModal}
        />
      )}
      <AssignFormModal
        isOpen={assignModalOpen}
        selectedWo={selectedWo}
        onClose={closeAssignModal}
        onWoSelect={(wo) => setSelectedWo(wo)}
        onAssign={handleAssignWo}
      />
      <UnassignFormModal
        isOpen={unassignModalOpen}
        woNumber={selectedFormForUnassign?.wo?.number}
        onClose={closeUnassignModal}
        onUnassign={handleUnassign}
      />
      <DeleteFormModal
        isOpen={deleteModalOpen}
        formName={selectedFormForDelete?.name}
        onClose={closeDeleteModal}
        onDelete={handleDelete}
      />
      <FormViewerModal
        shown={formViewerModalShown}
        title="PREVIEW FORM"
        forms={formsToPreview}
        onUnassign={() => {
          fetchForms(appliedFilters);
          setFormViewerModalShown(false);
          setFormsToPreview([]);
        }}
        onClose={() => {
          setFormViewerModalShown(false);
          setFormsToPreview([]);
        }}
      />

      {!hideControls && (
        <div className="w-full flex items-center justify-end px-5 py-2">
          <div className="flex items-center gap-8">
            <Button onClicked={() => setFilterOpen(!filterOpen)} title="Filter" isHighlighted={filterOpen} />
            {canCreate && <CreateButton onClicked={() => setCreateModalOpen(true)} title="CREATE" />}
          </div>
        </div>
      )}

      <div className={`flex-1 min-h-0 flex flex-col ${hideControls ? "" : "px-7"}`}>
        {!hideControls && filterOpen && (
          <div className="relative z-20">
            <FormFilterView onApply={handleApplyFilter} onClear={handleClearFilter} />
          </div>
        )}
        <div
          className="w-full mt-3 mb-3 flex-1 min-h-0 flex flex-col overflow-hidden"
          style={{
            borderRadius: "12px",
            border: "1px solid #E5E8F3",
            background: "#FFF",
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.00), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
          }}
        >
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
            <table className="w-full min-w-[1000px]">
              <thead className="sticky top-0 z-10">
                <tr
                  style={{ borderBottom: "1px solid #E5E8F3", background: "#F8F9FE" }}
                  className="[&>th]:border-r [&>th]:border-borderColor [&>th:last-child]:border-r-0"
                >
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center justify-center w-[56px] h-[44px] text-[#5F6477]">
                      No
                    </div>
                  </th>
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[200px] h-[44px] text-[#5F6477]">
                      Form Name
                    </div>
                  </th>
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[200px] h-[44px] text-[#5F6477]">
                      Type
                    </div>
                  </th>
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[140px] h-[44px] text-[#5F6477]">
                      Frequency
                    </div>
                  </th>
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center justify-center w-[110px] h-[44px] text-[#5F6477]">
                      Status
                    </div>
                  </th>
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[160px] h-[44px] text-[#5F6477]">
                      Technicians
                    </div>
                  </th>
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[100px] h-[44px] text-[#5F6477]">
                      Branch
                    </div>
                  </th>
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[140px] h-[44px] text-[#5F6477]">
                      Created By
                    </div>
                  </th>
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[110px] h-[44px] text-[#5F6477]">
                      WO#
                    </div>
                  </th>
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center justify-center w-[130px] h-[44px] text-[#5F6477]">
                      Date Created
                    </div>
                  </th>
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[20px] h-[44px] text-[#5F6477]"></div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {forms.map((form, index) => {
                  const status = getStatus(form.status);
                  return (
                    <tr
                      key={form.id}
                      className={`border-b border-borderColor cursor-pointer hover:bg-[#FAFAFB] [&>td]:border-r [&>td]:border-borderColor [&>td:last-child]:border-r-0`}
                      onClick={() => handleFormClick(form)}
                    >
                      <td>
                        <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332] text-center">
                          {index + 1}
                        </div>
                      </td>
                      <td>
                        <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332] word-break-break-word">
                          {form.name}
                        </div>
                      </td>
                      <td>
                        <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332]">
                          {getFormTypeLabel(form.formType)}
                        </div>
                      </td>
                      <td>
                        <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332]">
                          {form.frequency !== null && form.frequency !== undefined
                            ? getFrequencyLabel(form.frequency)
                            : ""}
                        </div>
                      </td>
                      <td className="text-center align-middle">
                        <div className="flex items-center justify-center px-4 py-3">
                          <div className="flex items-center justify-center w-[88px] h-[24px]">
                            <span
                              className={`inline-flex items-center justify-center w-[78px] py-[4px] rounded-full text-[10px] font-[600] font-OpenSansRegular ${status.bg} ${status.text}`}
                            >
                              {status.label}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332]">
                          {form.technicians && form.technicians.length > 0 ? (
                            <div className="flex items-center gap-1 flex-wrap">
                              {form.technicians.map((tech) => (
                                <div
                                  key={tech.id}
                                  title={tech.name}
                                  className="flex items-center gap-1 bg-[#F3F4F6] rounded-full pr-2 py-[2px]"
                                >
                                  {tech.avatar ? (
                                    <img
                                      src={getAvatarUrl(tech.avatar)}
                                      alt={tech.name || "Technician"}
                                      className="w-[16px] h-[16px] rounded-[8px]"
                                    />
                                  ) : (
                                    <div className="w-[16px] h-[16px] rounded-[8px] bg-[#D1D5DB]" />
                                  )}
                                  <span className="text-[10px] leading-none">{tech.name}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            ""
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332]">
                          {form.wo?.branch?.name || ""}
                        </div>
                      </td>
                      <td>
                        <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332] flex items-center gap-2">
                          {form.createdBy?.avatar && (
                            <img
                              src={getAvatarUrl(form.createdBy.avatar)}
                              alt={form.createdBy?.name || "Created by"}
                              className="w-[16px] h-[16px] rounded-[8px]"
                            />
                          )}
                          <span>{form.createdBy?.name || ""}</span>
                        </div>
                      </td>
                      <td>
                        <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332]">
                          {form.wo?.number ? `#${form.wo.number}` : ""}
                        </div>
                      </td>
                      <td>
                        <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332]">
                          {formatDate(form.createdAt)}
                        </div>
                      </td>
                      <td className="relative" onClick={(e) => e.stopPropagation()}>
                        <img
                          onClick={() => setActionMenuId(actionMenuId === form.id ? null : form.id)}
                          className="cursor-pointer"
                          src={getImageUrl("/images/common/options.png")}
                          alt="Options"
                        />
                        {actionMenuId === form.id && form.status === 0 && (
                          <DraftOptions
                            onCreateWo={() => handleCreateWo(form)}
                            onEdit={() => handleEditClick(form)}
                            onDuplicate={() => handleDuplicate(form)}
                            onDelete={() => handleDeleteClick(form)}
                            onClose={() => setActionMenuId(null)}
                            canAssign={canAssign}
                            canEdit={canUpdate}
                            canDuplicate={canDuplicate}
                            canDelete={canDelete}
                          />
                        )}
                        {actionMenuId === form.id && form.status === 1 && (
                          <AssignedOptions
                            onEdit={() => handleEditClick(form)}
                            onDuplicate={() => handleDuplicate(form)}
                            onUnassign={() => handleUnassignClick(form)}
                            onClose={() => setActionMenuId(null)}
                            canEdit={canUpdate}
                            canDuplicate={canDuplicate}
                            canUnassign={canUnassign}
                          />
                        )}
                        {actionMenuId === form.id && form.status === 2 && (
                          <PartiallyFilledOptions
                            onDuplicate={() => handleDuplicate(form)}
                            onClose={() => setActionMenuId(null)}
                            onUnassign={() => handleUnassignClick(form)}
                            canDuplicate={canDuplicate}
                            canUnassign={canUnassign}
                          />
                        )}
                        {actionMenuId === form.id && form.status === 3 && (
                          <FilledOptions
                            onDuplicate={() => handleDuplicate(form)}
                            onClose={() => setActionMenuId(null)}
                            onUnassign={() => handleUnassignClick(form)}
                            canDuplicate={canDuplicate}
                            canUnassign={canUnassign}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
                {forms.length === 0 && (
                  <tr>
                    <td colSpan={11} className="text-center py-10 text-[12px] font-OpenSansRegular text-subTitleColor">
                      No forms found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllForms;
